import React, { useContext, useState, useEffect, useRef } from 'react';
import throttle from 'lodash/throttle';
import clsx from 'clsx';
import LazyPicture from '../atoms/LazyPicture';
import { SitecoreContextReactContext } from '../../utils/SitecoreContextReactContext';
import { canUseDOM, get, injectImageResolution } from '../../utils';
import { tealiumClickFunc, tealiumMergeClickData } from '../../utils/tealium';
import { ReactComponent as ChevronLeftIcon } from '../../resources/icons/iconChevronLeftCollapsed.svg';
import { ReactComponent as ChevronRightIcon } from '../../resources/icons/iconChevronRightCollapsed.svg';
import { ReactComponent as ChevronDetLeftIcon } from '../../resources/icons/chevron-left-24-px.svg';
import { ReactComponent as ChevronDetRightIcon } from '../../resources/icons/chevron-right-24-px.svg';
const CategoryDetails = _ref => {
  let {
    cardsProps,
    cardsToShow,
    rtl,
    toTopTerm,
    additionalRecount,
    isMobile,
    inCarousel,
    backToTopHandler,
    analytics,
    isDetSite
  } = _ref;
  const {
    eventsAnalytics,
    params
  } = analytics;
  const title = get(cardsProps, 'title.jss.value');
  const column1Title = get(cardsProps, 'columnOneTitle.jss.value', '');
  const column1Description = get(cardsProps, 'columnOneDescription.jss.value', '');
  const column1CTATitle = get(cardsProps, 'columnOneLinkText.jss.value', '');
  const column1CTALink = get(cardsProps, 'columnOneLink.jss.value.href');
  const column1CTALinkLeftIcon = get(cardsProps, 'ctaOneLeftIcon.targetItem.svg.jss.value');
  const column1CTALinkRightIcon = get(cardsProps, 'ctaOneRightIcon.targetItem.svg.jss.value');
  const column2Title = get(cardsProps, 'columnTwoTitle.jss.value', '');
  const column2HTML = get(cardsProps, 'columnTwoText.jss.value', '');
  const column2CTATitle = get(cardsProps, 'columnTwoLinkText.jss.value', '');
  const column2CTALink = get(cardsProps, 'columnTwoLink.jss.value.href');
  const column2CTALinkLeftIcon = get(cardsProps, 'ctaTwoLeftIcon.targetItem.svg.jss.value');
  const column2CTALinkRightIcon = get(cardsProps, 'ctaTwoRightIcon.targetItem.svg.jss.value');
  const column3Image = get(cardsProps, 'columnThreeImage.jss.value');
  const column3Title = get(cardsProps, 'columnThreeTitle.jss.value', '');
  const column3CTALink = get(cardsProps, 'columnThreeLink.jss.value.href');
  const column4Image = get(cardsProps, 'columnFourImage.jss.value');
  const column4Title = get(cardsProps, 'columnFourTitle.jss.value', '');
  const column4CTALink = get(cardsProps, 'columnFourLink.jss.value.href');
  const sitecoreContext = useContext(SitecoreContextReactContext);
  const detailsWrapperRef = useRef(null);
  const [detailsHeight, setDetailsHeight] = useState(0);
  const [cardInfo, setCardInfo] = useState(0);
  const containerRef = useRef(null);
  const [column2CtaLabel, setColumn2CtaLabel] = useState('');
  let countHeight = 3;
  if (canUseDOM) {
    countHeight = throttle(() => {
      if (detailsWrapperRef.current) {
        let cardInfoHeight = 0;
        const cardInfoList = detailsWrapperRef.current.querySelectorAll('.wc-details__card-title');
        cardInfoList.forEach(el => {
          const height = el.offsetHeight;
          if (cardInfoHeight < height) cardInfoHeight = height;
        });
        setCardInfo(cardInfoHeight);
        setDetailsHeight(detailsWrapperRef.current.offsetHeight);
      }
    }, 300);
  }
  useEffect(() => {
    if (canUseDOM) {
      setTimeout(countHeight, 500);
      window.addEventListener('resize', countHeight);
    }
    return () => {
      window.removeEventListener('resize', countHeight);
    };
  }, []);
  useEffect(() => {
    if (canUseDOM && column2CTATitle && column2CTALink) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(column2HTML, "text/html");
      const columnPureText = doc && doc.body.textContent ? `${doc.body.textContent} | ` : '';
      const linkLabel = `${column2Title ? `${column2Title} | ` : ''}${columnPureText}${column2CTATitle}`;
      setColumn2CtaLabel(linkLabel);
    }
  }, [column2HTML, column2Title, column2CTATitle, column2CTALink]);
  useEffect(() => {
    if (additionalRecount && canUseDOM) {
      countHeight();
    }
  }, [additionalRecount]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleLinkClick = event => {
      const target = event.target;
      const link = target.closest('a');
      if (link) {
        const linkText = event.target.textContent;
        tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['categoryTitleAnalytics'], params), `${title} : ${column2Title} : ${linkText}`);
      }
    };
    container.addEventListener('click', handleLinkClick);
    return () => {
      container.removeEventListener('click', handleLinkClick);
    };
  }, []);
  useEffect(() => {
    if (canUseDOM) {
      setTimeout(countHeight, 100);
    }
  }, [sitecoreContext.selectedFontSize]);
  const backToTopClickHandler = e => {
    if (backToTopHandler) backToTopHandler(e);
  };
  const showColumn2 = column2Title || column2HTML || column2CTATitle;
  return /*#__PURE__*/React.createElement("div", {
    className: clsx('wc-details-visibility', {
      'wc-details-visibility--visible js--details-visible': cardsToShow.includes(cardsProps.id)
    }),
    style: {
      '--wc-details-height': `${detailsHeight}px`,
      '--wc-card-info-height': `${cardInfo}px`
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "wc-details-border",
    ref: detailsWrapperRef
  }, /*#__PURE__*/React.createElement("div", {
    className: "wc-details"
  }, /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-lg d-lg-flex"
  }, /*#__PURE__*/React.createElement("div", {
    className: clsx('wc-details__column-1', {
      'wc-details__column-1--fullwidth': !showColumn2
    })
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", null, column1Title), /*#__PURE__*/React.createElement("p", null, column1Description), column1CTATitle && column1CTALink && /*#__PURE__*/React.createElement("a", {
    className: "btn btn-primary",
    href: column1CTALink,
    onClick: () => {
      tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['subButtonClickAnalytics'], params), [title, column1CTATitle]);
    },
    "aria-label": `${column1Title ? `${column1Title} | ` : ''}${column1Description ? `${column1Description} | ` : ''}${column1CTATitle}`
  }, column1CTALinkLeftIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: column1CTALinkLeftIcon
    },
    className: "ctaLeftIcon"
  }) : /*#__PURE__*/React.createElement(React.Fragment, null), column1CTATitle, column1CTALinkRightIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: column1CTALinkRightIcon
    },
    className: "ctaRightIcon"
  }) : /*#__PURE__*/React.createElement(React.Fragment, null))))), showColumn2 && /*#__PURE__*/React.createElement("div", {
    className: "col-lg-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "wc-details__column-2"
  }, column2Title && /*#__PURE__*/React.createElement("h3", null, column2Title), column2HTML && /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    dangerouslySetInnerHTML: {
      __html: column2HTML
    },
    className: "wc-details__rtf"
  }), column2CTATitle && column2CTALink && /*#__PURE__*/React.createElement("a", {
    href: column2CTALink,
    onClick: () => {
      tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['subLearnMoreAnalytics'], params), [title, column2Title, column2CTATitle]);
    },
    "aria-label": column2CtaLabel
  }, column2CTALinkLeftIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: column2CTALinkLeftIcon
    },
    className: "ctaLeftIcon"
  }) : /*#__PURE__*/React.createElement(React.Fragment, null), column2CTATitle, column2CTALinkRightIcon ? /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: column2CTALinkRightIcon
    },
    className: "ctaRightIcon"
  }) : /*#__PURE__*/React.createElement(React.Fragment, null)))), column3CTALink && /*#__PURE__*/React.createElement("div", {
    className: "col-lg-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "wc-details__card"
  }, /*#__PURE__*/React.createElement("a", {
    href: column3CTALink,
    className: isDetSite ? "wc-details__card-image-infolink" : "",
    onClick: () => {
      tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['subCategoryBannerClickAnalytics'], params), [title, column3Title]);
    },
    "aria-label": column3Image.alt
  }, /*#__PURE__*/React.createElement(LazyPicture, {
    hover: true,
    useEmptyPlaceholder: true
  }, /*#__PURE__*/React.createElement("source", {
    media: "(min-width: 1441px)",
    srcSet: injectImageResolution(column3Image.src, 315, 230)
  }), /*#__PURE__*/React.createElement("source", {
    media: "(min-width: 992px)",
    srcSet: injectImageResolution(column3Image.src, 235, 170)
  }), /*#__PURE__*/React.createElement("source", {
    media: "(min-width: 568px)",
    srcSet: injectImageResolution(column3Image.src, 1750, 1270)
  }), /*#__PURE__*/React.createElement("img", {
    src: injectImageResolution(column3Image.src, 940, 680),
    alt: column3Image.alt
  }))), /*#__PURE__*/React.createElement("a", {
    href: column3CTALink,
    className: "wc-details__card-infolink",
    onClick: () => {
      tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['subCategoryTitleClickAnalytics'], params), [title, column3Title]);
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "wc-details__card-info"
  }, /*#__PURE__*/React.createElement("h4", {
    className: "wc-details__card-title"
  }, column3Title), /*#__PURE__*/React.createElement("span", {
    className: "spacer"
  }), isDetSite ? rtl ? /*#__PURE__*/React.createElement(ChevronDetLeftIcon, {
    className: "icon"
  }) : /*#__PURE__*/React.createElement(ChevronDetRightIcon, {
    className: "icon"
  }) : rtl ? /*#__PURE__*/React.createElement(ChevronLeftIcon, {
    className: "icon"
  }) : /*#__PURE__*/React.createElement(ChevronRightIcon, {
    className: "icon"
  }))))), column4CTALink && /*#__PURE__*/React.createElement("div", {
    className: "col-lg-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "wc-details__card"
  }, /*#__PURE__*/React.createElement("a", {
    href: column4CTALink,
    className: isDetSite ? "wc-details__card-image-infolink" : "",
    onClick: () => {
      tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['subCategoryBannerClickAnalytics'], params), [title, column4Title]);
    },
    "aria-label": column4Image.alt
  }, /*#__PURE__*/React.createElement(LazyPicture, {
    hover: true,
    useEmptyPlaceholder: true
  }, /*#__PURE__*/React.createElement("source", {
    media: "(min-width: 1441px)",
    srcSet: injectImageResolution(column4Image.src, 315, 230)
  }), /*#__PURE__*/React.createElement("source", {
    media: "(min-width: 992px)",
    srcSet: injectImageResolution(column4Image.src, 235, 170)
  }), /*#__PURE__*/React.createElement("source", {
    media: "(min-width: 568px)",
    srcSet: injectImageResolution(column4Image.src, 1750, 1270)
  }), /*#__PURE__*/React.createElement("img", {
    src: injectImageResolution(column4Image.src, 940, 680),
    alt: column4Image.alt
  }))), /*#__PURE__*/React.createElement("a", {
    href: column4CTALink,
    className: "wc-details__card-infolink",
    onClick: () => {
      tealiumClickFunc(tealiumMergeClickData(eventsAnalytics['subCategoryTitleClickAnalytics'], params), [title, column4Title]);
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "wc-details__card-info"
  }, /*#__PURE__*/React.createElement("h4", {
    className: "wc-details__card-title"
  }, column4Title), /*#__PURE__*/React.createElement("span", {
    className: "spacer"
  }), isDetSite ? rtl ? /*#__PURE__*/React.createElement(ChevronDetLeftIcon, {
    className: "icon"
  }) : /*#__PURE__*/React.createElement(ChevronDetRightIcon, {
    className: "icon"
  }) : rtl ? /*#__PURE__*/React.createElement(ChevronLeftIcon, {
    className: "icon"
  }) : /*#__PURE__*/React.createElement(ChevronRightIcon, {
    className: "icon"
  }))))))), inCarousel && isMobile && /*#__PURE__*/React.createElement("button", {
    className: "wc-details__top-link",
    onClick: backToTopClickHandler
  }, isDetSite ? /*#__PURE__*/React.createElement(ChevronDetRightIcon, {
    className: "icon"
  }) : /*#__PURE__*/React.createElement(ChevronRightIcon, {
    className: "icon"
  }), toTopTerm)));
};
export default CategoryDetails;